/**
 * Checklists service
 */

import { apiClient } from '../utils/api';
import type {
  ChecklistTemplate,
  ChecklistTemplateItem,
  ChecklistRun,
  ChecklistItemRun,
  ApiResponse,
} from '../../../shared/src/types';

export interface ChecklistTemplateWithItems {
  template: ChecklistTemplate;
  items: ChecklistTemplateItem[];
}

export interface ChecklistRunWithItems {
  run: ChecklistRun;
  items: ChecklistItemRun[];
}

export async function listTemplates(installationType?: string): Promise<ChecklistTemplate[]> {
  const queryParams = new URLSearchParams();
  if (installationType) queryParams.append('installation_type', installationType);
  queryParams.append('active', 'true');

  const url = `/checklists/templates${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await apiClient.get<ApiResponse<ChecklistTemplate[]>>(url);

  if (response.data.error) {
    throw new Error(response.data.error.message || 'Error fetching templates');
  }

  return response.data.data || [];
}

export async function getTemplate(id: string): Promise<ChecklistTemplateWithItems> {
  const response = await apiClient.get<ApiResponse<ChecklistTemplateWithItems>>(
    `/checklists/templates/${id}`
  );

  if (response.data.error) {
    throw new Error(response.data.error.message || 'Error fetching template');
  }

  if (!response.data.data) {
    throw new Error('Template not found');
  }

  return response.data.data;
}

export interface CreateChecklistRunData {
  installation_id: string;
  template_id: string;
}

export async function createChecklistRun(data: CreateChecklistRunData): Promise<ChecklistRun> {
  const response = await apiClient.post<ApiResponse<ChecklistRun>>('/checklists/runs', data);

  if (response.data.error) {
    throw new Error(response.data.error.message || 'Error creating checklist run');
  }

  if (!response.data.data) {
    throw new Error('Invalid response from server');
  }

  return response.data.data;
}

export async function getChecklistRun(id: string): Promise<ChecklistRunWithItems> {
  const response = await apiClient.get<ApiResponse<ChecklistRunWithItems>>(
    `/checklists/runs/${id}`
  );

  if (response.data.error) {
    throw new Error(response.data.error.message || 'Error fetching checklist run');
  }

  if (!response.data.data) {
    throw new Error('Checklist run not found');
  }

  return response.data.data;
}

export async function listChecklistRuns(installationId?: string): Promise<ChecklistRun[]> {
  const queryParams = new URLSearchParams();
  if (installationId) queryParams.append('installation_id', installationId);

  const url = `/checklists/runs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await apiClient.get<ApiResponse<ChecklistRun[]>>(url);

  if (response.data.error) {
    throw new Error(response.data.error.message || 'Error fetching checklist runs');
  }

  return response.data.data || [];
}

export interface UpdateChecklistItemData {
  status?: 'pending' | 'in_progress' | 'completed' | 'failed' | 'blocked';
  notes?: string;
  has_evidence?: boolean;
}

export async function updateChecklistItem(
  runId: string,
  itemId: string,
  data: UpdateChecklistItemData
): Promise<ChecklistItemRun> {
  const response = await apiClient.put<ApiResponse<ChecklistItemRun>>(
    `/checklists/runs/${runId}/items/${itemId}`,
    data
  );

  if (response.data.error) {
    throw new Error(response.data.error.message || 'Error updating checklist item');
  }

  if (!response.data.data) {
    throw new Error('Invalid response from server');
  }

  return response.data.data;
}

